import React, { useCallback, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Container, Image, Modal, Nav, Navbar, NavDropdown, OverlayTrigger, Tooltip } from 'react-bootstrap'
import './NavBar.scss'
import { useLayoutStore } from '../../state/layout.store'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { LANGUAGES } from '../../i18n'
import { gaEvent } from '../../libraries/utils'
import { useAuth } from '../AuthProvider'
import HeadwayWidget from '../External/HeadwayWidget'
import { authInstance } from '../../libraries/firebase'

const AppNavBar = () => {
  const location = useLocation()
  const { t } = useTranslation('layout')

  return (
    <Navbar variant="light" expand="md" className="container-limited" id="navbar-component">
      <Container>
        <Navbar.Brand as={Link} to="/">
          <Image src="/img/logo_orange.svg" width={160} height={21} className="logo" alt="Geartrack" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav">
          <i className="fa fa-bars fa-lg" />
        </Navbar.Toggle>
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav as="ul" className="me-auto menu-left ms-md-3">
            <Nav.Item as="li" className={isActive(location, '/')}>
              <Nav.Link as={Link} to="/">
                <span className="nav-link-icon d-md-none d-lg-inline-block">
                  <i className="far fa-box-alt" />
                </span>
                <span className="nav-link-title">{t('navigation.packages')}</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item as="li" className={isActive(location, '/roadmap')}>
              <Nav.Link as={Link} to="/roadmap">
                <span className="nav-link-icon d-md-none d-lg-inline-block">
                  <i className="far fa-road" />
                </span>
                <span className="nav-link-title">Roadmap</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item as="li" className={isActive(location, '/settings')}>
              <Nav.Link as={Link} to="/settings">
                <span className="nav-link-icon d-md-none d-lg-inline-block">
                  <i className="far fa-cog" />
                </span>
                <span className="nav-link-title">{t('title' as any, { ns: 'settings' })}</span>
              </Nav.Link>
            </Nav.Item>

            {/*<Nav.Item as="li" className={isActive(location, '/carriers')}>*/}
            {/*  <Nav.Link as={Link} to="/carriers">*/}
            {/*    <span className="nav-link-icon d-md-none d-lg-inline-block">*/}
            {/*      <i className="far fa-truck" />*/}
            {/*    </span>*/}
            {/*    <span className="nav-link-title">Carriers</span>*/}
            {/*  </Nav.Link>*/}
            {/*</Nav.Item>*/}
          </Nav>
          <Nav className="menu-right">
            <DarkModeItem />

            <Nav.Item>
              <Nav.Link href="#" active={false}>
                {/*
                 // @ts-ignore */}
                <HeadwayWidget account="7gn3Xx" id="HW_container">
                  <i className="far fa-rss fa-lg HW_icon" />
                </HeadwayWidget>
              </Nav.Link>
            </Nav.Item>

            <LanguageDropdown />

            <RightDropdown t={t} />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  )
}

const LanguageDropdown = () => {
  const { i18n } = useTranslation()

  return (
    <NavDropdown title={<i className="fa-lg far fa-globe" />} id="basic-nav-dropdown-lang">
      {LANGUAGES.map((l, i) => (
        <NavDropdown.Item key={i} onClick={_ => i18n.changeLanguage(l.code)} active={i18n.language === l.code}>
          <Image
            src={`/img/flags/${l.code === 'en' ? 'us' : l.code}.svg`}
            alt={l.description}
            width="15"
            className="me-2"
          />
          {l.description}
        </NavDropdown.Item>
      ))}
    </NavDropdown>
  )
}

const DarkModeItem = () => {
  const toggleDarkMode = useLayoutStore(state => state.toggleDarkMode)
  const darkMode = useLayoutStore(state => state.darkMode)
  const { t } = useTranslation()

  const onClickHandler = e => {
    e.stopPropagation()

    toggleDarkMode()
  }

  return (
    <Nav.Item>
      <OverlayTrigger
        key="bottom"
        placement="bottom"
        overlay={<Tooltip id={`tooltip`}>{t('navigation.toggle dark mode')}</Tooltip>}
      >
        <Nav.Link href="#" onClick={onClickHandler}>
          <i className={classNames('fa-lg', darkMode ? 'fa fa-sun' : 'far fa-moon')} />
        </Nav.Link>
      </OverlayTrigger>
    </Nav.Item>
  )
}

const RightDropdown = function ({ t }) {
  const [logoutModalVisible, setLogoutModalVisible] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const logoutClick = useCallback(() => {
    authInstance
      .signOut()
      .then(() => {
        navigate('/')
        gaEvent('logout')
      })
      .catch(e => {
        console.error(e)
      })
  }, [navigate])

  const handleClose = useCallback(() => {
    setLogoutModalVisible(false)
  }, [])

  const handleOpen = useCallback(() => {
    setLogoutModalVisible(true)
  }, [])

  if (!user) {
    return (
      <Nav.Item as="li" className={isActive(location, '/login')}>
        <Nav.Link as={Link} to="/login" className="border border-1">
          <span className="nav-link-icon d-md-none d-lg-inline-block">
            <i className="far fa-user" />
          </span>
          <span>{t('navigation.sign in')}</span>
        </Nav.Link>
      </Nav.Item>
    )
  }

  return (
    <NavDropdown title={<Greeting name={user.name} />} id="basic-nav-dropdown-user">
      <NavDropdown.Item onClick={handleOpen}>
        <i className="fa sign-out-alt me-2" />
        {t('navigation.logout')}
      </NavDropdown.Item>
      <LogoutModal visible={logoutModalVisible} handleClose={handleClose} handleSubmit={logoutClick} />
    </NavDropdown>
  )
}

const Greeting = function ({ name }: { name: string }) {
  return (
    <span className="username text-warning">
      <strong>{name}</strong>
    </span>
  )
}

const LogoutModal = ({ visible, handleClose, handleSubmit }) => {
  return (
    <Modal show={visible} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Logout</Modal.Title>
      </Modal.Header>
      <Modal.Body>Your packages are saved on the server, login again to retrieve them.</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Logout
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const isActive = (location, path, className = 'active') => {
  return location.pathname === path ? className : ''
}

export default AppNavBar
